import { ComponentType } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Spin } from "antd";
import { useHasPermissions } from "auth/hooks";
import { useBoundStore } from "store";

type Props = {
  ComponentRoute: ComponentType;
  permissionsNeededToAccessRoute?: string[];
};

export const ProtectedRoute = ({
  ComponentRoute,
  permissionsNeededToAccessRoute = [],
}: Props) => {
  const { isLoading, isAuthenticated, loginWithRedirect, error } = useAuth0();
  const location = useLocation();
  const { hasPermissions, isLoading: permissionsLoading } = useHasPermissions();
  const { isAxiosInterceptorsReady } = useBoundStore();

  // Don't trigger anything until loading is finished
  if (isLoading) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Spin /></div>;
  }

  // Wait for axios interceptors to be ready before rendering protected content
  if (isAuthenticated && !isAxiosInterceptorsReady) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Spin /></div>;
  }

  if (error) {
    return <div>Auth error: {error.message}</div>;
  }

  if (!isAuthenticated) {
    // if you want to auto-redirect
    loginWithRedirect({
      appState: { returnTo: location.pathname + location.search },
      authorizationParams: {
        screen_hint: "login",
      },
    });
    return null;
  }

  // Wait for permissions to load before deciding access
  if (permissionsLoading && permissionsNeededToAccessRoute.length > 0) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Spin /></div>;
  }

  if (!hasPermissions(permissionsNeededToAccessRoute)) {
    return <Navigate to='/forbidden' replace />;
  }

  return <ComponentRoute />;
};
